import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, ArrowLeft, User } from 'lucide-react';
import PageTransition from '../components/PageTransition';
import { COMMITTEES, NAVBAR_CTA } from '../data';
import './CommitteeDetailPage.css';

export default function CommitteeDetailPage() {
  const { slug } = useParams();
  const c = COMMITTEES.find(c => c.slug === slug);
  if (!c) return <Navigate to="/komiteler" replace />;

  return (
    <PageTransition>
      <div className="committee-detail-page">

        {/* Hero */}
        <section className="detail-hero" style={{ '--accent': c.color }}>
          <div className="detail-hero-bg" />
          <div className="container detail-hero-content">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Link to="/komiteler" className="back-link">
                <ArrowLeft size={15} /> Tüm Komiteler
              </Link>
              <div className="detail-hero-header">
                <span className="detail-icon">{c.icon}</span>
                <div>
                  <span
                    className="detail-badge"
                    style={{ color: c.color, background: `${c.color}22`, border: `1px solid ${c.color}44` }}
                  >
                    {c.difficulty} · {c.delegate_count} Kontenjan
                  </span>
                  <h1>{c.title}</h1>
                  <p className="detail-subtitle">{c.subtitle}</p>
                </div>
              </div>
              <p className="detail-desc">{c.description}</p>
            </motion.div>
          </div>
          <div className="detail-accent-bar" style={{ background: `linear-gradient(90deg, ${c.color}, var(--orange))` }} />
        </section>

        {/* Body */}
        <section className="detail-body">
          <div className="container detail-body-grid">

            {/* Main */}
            <div className="detail-main">

              {/* Topics */}
              <motion.div className="detail-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2>Tartışma Konuları</h2>
                <p className="detail-card-intro">
                  Bu dönem komitemiz aşağıdaki gündem maddelerini ele alacaktır. Her konu için kapsamlı araştırma yapmanız beklenmektedir.
                </p>
                <ol className="topics-list">
                  {c.topics.map((topic, i) => (
                    <li key={i} style={{ '--dot-color': c.color }}>
                      <span className="topic-num">{String(i + 1).padStart(2, '0')}</span>
                      <span className="topic-text">{topic}</span>
                    </li>
                  ))}
                </ol>
              </motion.div>

              {/* Moderators */}
              <motion.div className="detail-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2>Komite Moderatörleri</h2>
                <p className="detail-card-intro">
                  Deneyimli lise öğrencisi moderatörlerimiz tartışmaları yönlendirecektir.
                </p>
                <div className="moderators-grid">
                  {c.moderators.map((mod, i) => (
                    <div key={i} className="moderator-card">
                      <div className="moderator-avatar" style={{ background: `linear-gradient(135deg, ${c.color}44, ${c.color}22)` }}>
                        <User size={26} color={c.color} />
                      </div>
                      <div className="moderator-info">
                        <h3>{mod.name}</h3>
                        <span className="moderator-title">{mod.title}</span>
                        <p>{mod.bio}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

            </div>

            {/* Sidebar */}
            <div className="detail-sidebar">

              <motion.div className="sidebar-card guide-card" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <div className="guide-icon">📄</div>
                <h3>Akademik Rehber</h3>
                <p>Komite çalışmalarına hazırlık için kapsamlı rehber dökümanını indirin.</p>
                <a href={c.guideFile} className="download-btn" download>
                  <Download size={15} />
                  Rehberi İndir (PDF)
                </a>
              </motion.div>

              <motion.div className="sidebar-card info-card" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
                <h3>Komite Bilgileri</h3>
                <ul className="info-list">
                  <li>
                    <span className="info-label">Seviye</span>
                    <span className={`difficulty difficulty-${c.difficulty === 'Başlangıç' ? 'easy' : c.difficulty === 'Orta' ? 'medium' : 'hard'}`}>{c.difficulty}</span>
                  </li>
                  <li><span className="info-label">Kontenjan</span><span className="info-value">{c.delegate_count} Delege</span></li>
                  <li><span className="info-label">Moderatör</span><span className="info-value">{c.moderators.length} Kişi</span></li>
                  <li><span className="info-label">Gündem</span><span className="info-value">{c.topics.length} Konu</span></li>
                </ul>
              </motion.div>

              <motion.div className="sidebar-card apply-card" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                <h3>Bu Komiteye Başvur</h3>
                <p>Kontenjanlar sınırlıdır. Başvurunu hemen yap.</p>
                <Link to={NAVBAR_CTA.path} className="btn-primary" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  Başvuru Formuna Git →
                </Link>
              </motion.div>

            </div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
}
